import type { AppointmentType, Type } from '@mend/types/appointment';
import type { User } from '@mend/types/user';
import type { DirectoryProvider } from '#services/api/provider.ts';

import { useState } from 'react';
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FontAwesomeScalableIcon } from '@mend/mui';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AppointmentTypesAutocomplete } from '#components/Autocomplete/AppointmentTypesAutocomplete.tsx';
import { ProvidersAutocomplete } from '#components/Autocomplete/Providers/ProvidersAutocomplete.tsx';
import { ComprehensivePhoneOption } from '#components/Autocomplete/Users/ListItems/ComprehensivePhoneOption.tsx';
import { UsersAutocomplete } from '#components/Autocomplete/Users/UsersAutocomplete.tsx';
import { activeWaitlist, seededReadQueryKeys } from '#lib/react-query/queries.ts';
import { enrollByStaff, getStaffEnrollmentErrorMessage } from '#services/api/mia.ts';

/** Matches the coordinator's nonVisitAppointmentBaseTypes admission convention. */
const nonVisitAppointmentBaseTypes: readonly Type[] = [
  'Assessment',
  'Recall',
  'Unavailable',
  'Messaging',
  'Group Messaging',
];
const schema = z.object({
  patient: z
    .custom<User>()
    .nullable()
    .refine((patient): patient is User => patient !== null, {
      message: 'Select a patient',
    }),
  provider: z
    .custom<DirectoryProvider>()
    .nullable()
    .refine((provider): provider is DirectoryProvider => provider !== null, {
      message: 'Select a provider',
    }),
  appointmentType: z
    .custom<AppointmentType>()
    .nullable()
    .refine((appointmentType): appointmentType is AppointmentType => appointmentType !== null, {
      message: 'Select a visit type',
    }),
});
const defaultValues: z.input<typeof schema> = {
  patient: null,
  provider: null,
  appointmentType: null,
};

/** Records specific criteria using the existing organization directories, not dashboard filters. */
export function WaitlistEnrollmentForm(props: { orgId: number; onClose: () => void }) {
  // Key to reset the pickers' internal state. Needed because they're partially unaffected by
  // `form.reset()`.
  const [formResetKey, setFormResetKey] = useState(0);
  const form = useForm<z.input<typeof schema>, unknown, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const queryClient = useQueryClient();
  const staffEnrollment = useMutation({
    mutationFn: enrollByStaff,
    onSuccess: async (entry, request) => {
      const { queryKey } = activeWaitlist({ orgId: request.orgId });
      // Prevent an older refetch from undoing the merge into the loaded full snapshot.
      await queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;
        // A refetch may have included this enrollment before cancellation completed.
        const hasEntry = current.entries.some((currentEntry) => currentEntry.id === entry.id);
        const entries = current.entries.filter((currentEntry) => currentEntry.id !== entry.id);
        entries.push(entry);
        entries.sort(
          (left, right) =>
            Date.parse(left.createdAt) - Date.parse(right.createdAt) || left.id - right.id
        );
        return {
          ...current,
          entries,
          totalCount: hasEntry ? current.totalCount : current.totalCount + 1,
        };
      });
      void queryClient.invalidateQueries({ queryKey: seededReadQueryKeys.offerableSlots });
      enqueueSnackbar('Patient added to waitlist.', { variant: 'success' });
      form.reset();
      // Reset the pickers' internal search text and options as well as their selected values.
      // Needed because the components maintain an internal controlled state which is unaffected by
      // `form.reset()`.
      setFormResetKey((key) => key + 1);
    },
  });

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit((values) =>
          staffEnrollment.mutate({
            orgId: props.orgId,
            patientId: values.patient.id,
            providerId: values.provider.id,
            appointmentTypeId: values.appointmentType.id,
          })
        )(event);
      }}
      direction="column"
      spacing={2}
      sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
    >
      <Typography variant="caption" component="p" textTransform="uppercase" color="text.secondary">
        Add a patient to the waitlist
      </Typography>
      {staffEnrollment.isError && (
        <Alert severity="error" onClose={staffEnrollment.reset}>
          {getStaffEnrollmentErrorMessage(staffEnrollment.error)}
        </Alert>
      )}
      <Stack key={formResetKey} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          control={form.control}
          name="patient"
          render={({ field, fieldState }) => (
            <UsersAutocomplete
              Option={ComprehensivePhoneOption}
              getOptionDisabled={(option) => !option.mobile}
              disabled={staffEnrollment.isPending}
              value={field.value}
              onChange={(_event, value) => field.onChange(value)}
              onBlur={field.onBlur}
              inputName={field.name}
              inputLabel="Patient"
              inputRequired
              inputError={Boolean(fieldState.error)}
              inputHelperText={fieldState.error?.message}
              getUsersParams={() => ({ role: ['Patient'] })}
            />
          )}
        />
        <Controller
          control={form.control}
          name="provider"
          render={({ field, fieldState }) => (
            <ProvidersAutocomplete
              multiple={false}
              includeUnassigned={false}
              disabled={staffEnrollment.isPending}
              value={field.value}
              onChange={(_event, value) => field.onChange(value)}
              onBlur={field.onBlur}
              inputName={field.name}
              inputLabel="Provider"
              inputRequired
              inputError={Boolean(fieldState.error)}
              inputHelperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={form.control}
          name="appointmentType"
          render={({ field, fieldState }) => (
            <AppointmentTypesAutocomplete
              multiple={false}
              disabled={staffEnrollment.isPending}
              value={field.value}
              onChange={(_event, value) => field.onChange(value)}
              onBlur={field.onBlur}
              inputName={field.name}
              inputLabel="Visit type"
              inputRequired
              inputError={Boolean(fieldState.error)}
              inputHelperText={fieldState.error?.message}
              preFilterOptions={(options) =>
                options.filter((type) => !nonVisitAppointmentBaseTypes.includes(type.type))
              }
            />
          )}
        />
      </Stack>

      <Typography variant="caption" component="p" color="text.secondary">
        Coordinate only offers slots that match the provider and visit type above.
      </Typography>

      <Stack direction="row" spacing={1} justifyContent="flex-start">
        <Button
          type="submit"
          color="quaternary"
          variant="contained"
          loading={staffEnrollment.isPending}
          disabled={!form.formState.isDirty}
          startIcon={<FontAwesomeScalableIcon icon={faUserPlus} fontSize="small" />}
        >
          Add to waitlist
        </Button>

        <Button
          variant="outlined"
          color="neutral"
          onClick={props.onClose}
          disabled={staffEnrollment.isPending}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
}
